import React from "react";
import { motion } from "framer-motion";
import NavBar from "@/components/NavBar";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import {
  ArrowRight,
  CheckCircle,
  Zap,
  Users,
  BarChart3,
  Rocket,
  Clock,
  Shield,
  TrendingUp,
  Star,
} from "lucide-react";

const HowItWorks = () => {
  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    show: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } },
  };

  const cardVariants = {
    hidden: { opacity: 0, scale: 0.95 },
    show: {
      opacity: 1,
      scale: 1,
      transition: { duration: 0.5, ease: "easeOut" },
    },
  };

  // Process steps data
  const steps = [
    {
      number: "01",
      title: "Strategic Campaign Design",
      description:
        "Define your value proposition, identify target segments, and craft validation hypotheses. Our intelligent framework guides you through evidence-based question design to maximize actionable insights.",
      icon: Zap,
      color: "from-blue-500 to-indigo-600",
    },
    {
      number: "02",
      title: "Multi-Channel Distribution",
      description:
        "Deploy your validation campaign across multiple touchpoints. Leverage our distribution network and sharing tools to reach your ideal customer profile with precision targeting.",
      icon: Users,
      color: "from-emerald-500 to-teal-600",
    },
    {
      number: "03",
      title: "Real-Time Analytics Dashboard",
      description:
        "Monitor engagement metrics, response patterns, and sentiment analysis through our comprehensive analytics suite. Track conversion funnels and identify optimization opportunities instantly.",
      icon: BarChart3,
      color: "from-purple-500 to-violet-600",
    },
    {
      number: "04",
      title: "Data-Driven Decision Making",
      description:
        "Transform raw feedback into strategic recommendations. Our AI-powered insights engine identifies market opportunities, validates assumptions, and provides clear go/no-go recommendations.",
      icon: Rocket,
      color: "from-orange-500 to-red-600",
    },
  ];

  // Key benefits with metrics
  const benefits = [
    {
      icon: Clock,
      title: "90% Faster Validation",
      description: "Accelerate your validation process from months to weeks",
    },
    {
      icon: Shield,
      title: "Risk Mitigation",
      description:
        "Identify fatal flaws before investing significant resources",
    },
    {
      icon: TrendingUp,
      title: "Higher Success Rates",
      description: "3x better product-market fit achievement rates",
    },
    {
      icon: Users,
      title: "Early Customer Acquisition",
      description: "Build your initial customer base during validation",
    },
    {
      icon: BarChart3,
      title: "Quantified Market Insights",
      description: "Data-backed evidence for investor presentations",
    },
    {
      icon: Star,
      title: "Competitive Intelligence",
      description:
        "Understand market positioning and differentiation opportunities",
    },
  ];

  // Success metrics
  const metrics = [
    { value: "10,000+", label: "Ideas Validated", color: "text-blue-600" },
    { value: "85%", label: "Success Rate", color: "text-emerald-600" },
    { value: "60 Days", label: "Average Time Saved", color: "text-purple-600" },
    { value: "95%", label: "User Satisfaction", color: "text-orange-600" },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <NavBar />

      <div className="container mx-auto px-4 pt-24 pb-16">
        <div className="max-w-6xl mx-auto">
          {/* Hero Section */}
          <motion.div
            className="text-center mb-20"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
          >
            <div className="inline-flex items-center px-4 py-2 bg-blue-50 rounded-full text-blue-700 text-sm font-medium mb-6 border border-blue-100">
              <Zap className="w-4 h-4 mr-2" />
              Enterprise-Grade Validation Platform
            </div>
            <h1 className="text-5xl md:text-6xl font-bold mb-6 leading-tight">
              Validate Ideas with
              <span className="text-gradient block mt-2">
                Scientific Precision
              </span>
            </h1>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto mb-8 leading-relaxed">
              Transform your innovation process with our comprehensive
              validation methodology. Reduce market risk, accelerate
              time-to-market, and build products your customers actually want.
            </p>

            {/* Metrics Bar */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-10 max-w-4xl mx-auto">
              {metrics.map((metric, index) => (
                <motion.div
                  key={index}
                  className="text-center"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.2 + index * 0.1 }}
                >
                  <div className={`text-3xl font-bold ${metric.color} mb-1`}>
                    {metric.value}
                  </div>
                  <div className="text-slate-600 text-sm">{metric.label}</div>
                </motion.div>
              ))}
            </div>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                asChild
                size="lg"
                className="bg-primary hover:bg-primary/90 text-white px-8 py-4 text-lg rounded-full shadow-lg hover:shadow-xl transition-all duration-300"
              >
                <Link to="/create">
                  Start Your Validation Journey
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
              </Button>
              <Button
                variant="outline"
                size="lg"
                className="px-8 py-4 text-lg rounded-full border-2 hover:bg-slate-50"
                asChild
              >
                <Link to="/browse">Explore Success Stories</Link>
              </Button>
            </div>
          </motion.div>

          {/* Methodology Section */}
          <motion.div
            className="mb-24"
            variants={containerVariants}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.2 }}
          >
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold mb-4">
                Our Proven Methodology
              </h2>
              <p className="text-xl text-slate-600 max-w-2xl mx-auto">
                Four strategic phases designed to de-risk your innovation and
                maximize market success
              </p>
            </div>

            <div className="space-y-20">
              {steps.map((step, index) => (
                <motion.div
                  key={index}
                  variants={itemVariants}
                  className={`flex flex-col ${
                    index % 2 !== 0 ? "lg:flex-row-reverse" : "lg:flex-row"
                  } gap-12 items-center`}
                >
                  <div className="w-full lg:w-1/2 space-y-6">
                    <div className="flex items-center gap-4">
                      <div className="text-blue-600 font-mono text-2xl font-bold bg-blue-50 px-4 py-2 rounded-lg">
                        {step.number}
                      </div>
                      <div
                        className={`p-3 rounded-xl bg-gradient-to-r ${step.color} text-white`}
                      >
                        <step.icon className="w-6 h-6" />
                      </div>
                    </div>
                    <h3 className="text-3xl font-bold text-slate-900">
                      {step.title}
                    </h3>
                    <p className="text-lg text-slate-600 leading-relaxed">
                      {step.description}
                    </p>
                  </div>

                  <div className="w-full lg:w-1/2">
                    <div
                      className={`bg-gradient-to-br ${step.color} rounded-2xl p-8 text-white shadow-2xl`}
                    >
                      <div className="h-64 flex items-center justify-center">
                        <step.icon className="w-24 h-24 opacity-20" />
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Benefits Grid */}
          <motion.div
            className="mb-24"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true, amount: 0.2 }}
          >
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold mb-4">
                Why Industry Leaders Choose FlingORFund
              </h2>
              <p className="text-xl text-slate-600 max-w-2xl mx-auto">
                Comprehensive validation capabilities that transform how you
                approach innovation
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {benefits.map((benefit, index) => (
                <motion.div
                  key={index}
                  variants={cardVariants}
                  initial="hidden"
                  whileInView="show"
                  viewport={{ once: true, amount: 0.2 }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 border border-slate-100 group hover:border-blue-200"
                >
                  <div className="bg-gradient-to-br from-blue-50 to-indigo-50 w-16 h-16 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                    <benefit.icon className="w-8 h-8 text-blue-600" />
                  </div>
                  <h3 className="text-xl font-bold text-slate-900 mb-3">
                    {benefit.title}
                  </h3>
                  <p className="text-slate-600 leading-relaxed">
                    {benefit.description}
                  </p>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Sample Campaign Preview */}
          <motion.div
            className="mb-24"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true, amount: 0.2 }}
          >
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold mb-4">
                See Our Platform in Action
              </h2>
              <p className="text-xl text-slate-600 max-w-2xl mx-auto">
                Explore a live validation campaign and experience the interface
                your customers will use
              </p>
            </div>

            <div className="bg-white rounded-3xl border border-slate-200 overflow-hidden shadow-2xl hover:shadow-3xl transition-all duration-500 group">
              <AspectRatio ratio={16 / 10}>
                <div className="w-full h-full bg-gradient-to-br from-slate-100 via-blue-50 to-indigo-100 flex items-center justify-center relative overflow-hidden">
                  <div className="absolute inset-0 bg-gradient-to-r from-blue-600/10 to-indigo-600/10"></div>
                  <div className="text-center z-10">
                    <div className="w-20 h-20 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-2xl flex items-center justify-center mb-6 mx-auto group-hover:scale-110 transition-transform duration-300">
                      <Rocket className="w-10 h-10 text-white" />
                    </div>
                    <div className="text-2xl font-bold text-slate-800 mb-2">
                      Interactive Campaign Demo
                    </div>
                    <div className="text-slate-600">
                      Experience our validation platform firsthand
                    </div>
                  </div>
                </div>
              </AspectRatio>
              <div className="p-8">
                <div className="flex items-start justify-between mb-6">
                  <div>
                    <h3 className="text-2xl font-bold text-slate-900 mb-2">
                      TaskFlow AI - Intelligent Task Management
                    </h3>
                    <p className="text-slate-600 text-lg leading-relaxed">
                      AI-powered productivity platform that adapts to your
                      workflow patterns and optimizes task prioritization based
                      on energy levels, deadlines, and personal productivity
                      rhythms.
                    </p>
                  </div>
                  <div className="bg-emerald-100 text-emerald-800 px-4 py-2 rounded-full text-sm font-medium ml-6">
                    Live Campaign
                  </div>
                </div>
                <div className="flex flex-col sm:flex-row gap-4">
                  <Button
                    asChild
                    size="lg"
                    className="bg-primary hover:bg-primary/90 text-white rounded-full"
                  >
                    <Link to="/campaign/1">
                      Explore Live Campaign
                      <ArrowRight className="ml-2 h-5 w-5" />
                    </Link>
                  </Button>
                  <Button
                    variant="outline"
                    size="lg"
                    className="rounded-full border-2"
                    asChild
                  >
                    <Link to="/browse">View All Success Stories</Link>
                  </Button>
                </div>
              </div>
            </div>
          </motion.div>

          {/* CTA Section */}
          <motion.div
            className="text-center bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 rounded-3xl p-12 text-white relative overflow-hidden"
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true, amount: 0.2 }}
          >
            <div className="absolute inset-0 bg-gradient-to-r from-white/10 to-transparent"></div>
            <div className="relative z-10">
              <h2 className="text-4xl md:text-5xl font-bold mb-6">
                Ready to Transform Your Innovation Process?
              </h2>
              <p className="text-xl text-blue-100 max-w-2xl mx-auto mb-8 leading-relaxed">
                Join thousands of entrepreneurs and enterprises who've validated
                their ideas with confidence. Start your strategic validation
                journey today.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button
                  asChild
                  size="lg"
                  className="bg-white text-blue-600 hover:bg-blue-50 px-8 py-4 text-lg rounded-full shadow-lg hover:shadow-xl transition-all duration-300"
                >
                  <Link to="/create">
                    Launch Your Campaign
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Link>
                </Button>
                <Button
                  variant="outline"
                  size="lg"
                  className="border-2 border-white/30 text-white hover:bg-white/10 px-8 py-4 text-lg rounded-full"
                  asChild
                >
                  <Link to="/register">Start Free Trial</Link>
                </Button>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default HowItWorks;
